import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, GripVertical, Trash2, FileText, Image, Video } from 'lucide-react';
import { AdminModuleSlide, useDeleteSlide } from '@/hooks/useEducationAdmin';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';

interface SlideCardProps {
  slide: AdminModuleSlide;
  onEdit: (slide: AdminModuleSlide) => void;
}

const getSlideIcon = (type: string) => {
  switch (type) {
    case 'image':
      return <Image className="h-4 w-4" />;
    case 'video':
      return <Video className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

export const SlideCard: React.FC<SlideCardProps> = ({ slide, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const deleteSlide = useDeleteSlide();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDelete = async () => {
    await deleteSlide.mutateAsync({ id: slide.id, moduleId: slide.module_id });
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`transition-all duration-200 ${isDragging ? 'shadow-lg scale-105' : ''}`}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-3">
          <div
            className="cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex items-center space-x-2">
            {getSlideIcon(slide.slide_type)}
            <CardTitle className="text-base">{slide.title}</CardTitle>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline">#{slide.sort_order}</Badge>
          <Badge variant="secondary">{slide.slide_type}</Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {slide.content && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {slide.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
          </p>
        )}
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(slide)}
          >
            <Edit className="mr-1 h-3 w-3" />
            Edit
          </Button>
          
          <ConfirmDialog
            title="Delete Slide"
            description="Are you sure you want to delete this slide? This action cannot be undone."
            confirmText="Delete"
            variant="destructive"
            onConfirm={handleDelete}
            isLoading={deleteSlide.isPending}
            trigger={
              <Button variant="outline" size="sm">
                <Trash2 className="mr-1 h-3 w-3" />
                Delete
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};